/**
 * Toast.js — single toast component.
 *
 * Renders a fixed-position toast at the bottom-right. Owns its own expiry
 * timer; when it fires it calls onExpire. Action button (optional) calls
 * onAction and signals dismissal via the same onAction (parent decides to
 * clear). Error variant is visually distinct.
 *
 * Used via <ToastHost /> + useToast() hook — do NOT mount directly in a page.
 */

import React, { useEffect, useRef } from 'react';
import { colors } from './AppLayout';


function Toast({
  message,
  actionLabel = null,
  onAction = null,
  variant = 'info',
  durationMs = 5000,
  onExpire = null,
}) {
  // Keep the latest onExpire in a ref so the timer effect doesn't reset when
  // the callback identity changes. ToastHost creates fresh closures on every
  // render, and any ancestor re-render (sidebar toggle, page state change)
  // propagates to the toast — without this, a 5s toast could live
  // indefinitely while the user interacts with the page.
  const onExpireRef = useRef(onExpire);
  useEffect(() => {
    onExpireRef.current = onExpire;
  });

  useEffect(() => {
    if (durationMs <= 0) return undefined;
    const t = setTimeout(() => {
      const cb = onExpireRef.current;
      if (cb) cb();
    }, durationMs);
    return () => clearTimeout(t);
    // Intentionally re-run timer only when the message / variant / duration
    // changes so a replacement toast gets its full duration.
  }, [message, variant, durationMs]);

  const isError = variant === 'error';
  const background = isError ? '#FEF2F2' : colors.deepBlue;
  const textColor = isError ? '#991B1B' : colors.white;
  const borderColor = isError ? '#FECACA' : 'transparent';

  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        position: 'fixed',
        bottom: '24px',
        right: '24px',
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        gap: '16px',
        padding: '12px 16px',
        minWidth: '280px',
        maxWidth: '420px',
        backgroundColor: background,
        color: textColor,
        border: `1px solid ${borderColor}`,
        borderRadius: '8px',
        boxShadow: 'var(--shadow-lg, 0 10px 25px rgba(0,0,0,0.15))',
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        fontSize: 'var(--font-size-sm, 13px)',
        lineHeight: 1.45,
      }}
    >
      {isError && (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
             stroke="#991B1B" strokeWidth="2" strokeLinecap="round"
             strokeLinejoin="round" style={{ flexShrink: 0 }}>
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="8" x2="12" y2="12" />
          <line x1="12" y1="16" x2="12.01" y2="16" />
        </svg>
      )}

      <div style={{ flex: 1, minWidth: 0 }}>{message}</div>

      {actionLabel && onAction && (
        <button
          type="button"
          onClick={onAction}
          style={{
            background: 'transparent',
            border: `1px solid ${isError ? '#991B1B' : 'rgba(255,255,255,0.35)'}`,
            color: textColor,
            fontWeight: 600,
            fontSize: 'var(--font-size-sm, 13px)',
            padding: '6px 12px',
            borderRadius: '6px',
            cursor: 'pointer',
            whiteSpace: 'nowrap',
          }}
        >
          {actionLabel}
        </button>
      )}
    </div>
  );
}

export default Toast;
